import {
  PdfChallenge,
  ChallengeType,
  PdfData,
  RemainingChallenges,
  Score,
  TeamReference,
} from "@/models/pdfData";
import { otherTeam } from "@/pdf/utils";
import {
  Challenge,
  CompletedChallenge,
  DeclinedChallenge,
  SamsChallengeMoment,
  SamsChallengeType,
  StartedChallenge,
  SuccessfulChallenge,
  UnresolvedChallenge,
  UnsuccessfulChallenge,
} from "@/models/gameState";
import { ChallengeSuccessPayload } from "@/models/socketIo/updateAction";

export const sortChallenges = (
  challenges: Challenge[],
  order: "ascending" | "descending" = "descending",
): Challenge[] => {
  const sumOfScore = (score: Score): number => score.team2 + score.team1;
  const orderMultiplyer = order === "descending" ? 1 : -1;
  return challenges.sort((c1, c2) => {
    const setDif = c1.setNumber - c2.setNumber;
    if (setDif) {
      return orderMultiplyer * setDif;
    } else {
      const pointDif =
        sumOfScore(c1.setScoreBeforeChallenge) -
        sumOfScore(c2.setScoreBeforeChallenge);
      if (pointDif) {
        return orderMultiplyer * pointDif;
      } else {
        return orderMultiplyer * c2.challenger.localeCompare(c1.challenger);
      }
    }
  });
};

export const remainingChallenges = (
  game: PdfData,
  sets: number[] = Array.from({ length: 5 }, (_, i) => i + 1),
): RemainingChallenges =>
  sets
    .map((s) => {
      if (game) {
        return {
          [s]: {
            team1:
              2 -
              game.challenges.filter(
                (c) =>
                  c.setNumber === s && countsAsUsedChallengeForTeam("team1", c),
              ).length,
            team2:
              2 -
              game.challenges.filter(
                (c) =>
                  c.setNumber === s && countsAsUsedChallengeForTeam("team2", c),
              ).length,
          },
        };
      } else {
        return { [s]: { team1: 2, team2: 2 } };
      }
    })
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

export const countsAsUsedChallengeForTeam = (
  team: TeamReference,
  challenge: CompletedChallenge,
): boolean => {
  const takenByTeam = challenge.challenger === team;
  const challengeNotSuccessful = !challenge.successful;
  const interruptingChallenge = challenge.challengeMoment === "DURING_RALLY";
  const challengeNotResolvable = !!challenge.unresolved;

  return (
    takenByTeam &&
    (challengeNotSuccessful ||
      (interruptingChallenge && challengeNotResolvable))
  );
};

export const challengeComplete = (
  challenge: PdfChallenge | undefined,
): boolean => {
  if (!challenge) {
    return false;
  } else {
    const {
      challengeResult,
      set,
      typeOfChallenge,
      team,
      scoreAfterChallenge,
      scoreRallyStart,
      scoreRallyEnd,
    } = challenge;

    const mandatoryFields = [
      challengeResult,
      set,
      typeOfChallenge,
      team,
      scoreAfterChallenge,
    ];

    const allMandatoryFieldsExist = mandatoryFields
      .map((field) => !!field)
      .every(Boolean);
    const oneOfScoresExists = !!(scoreRallyStart || scoreRallyEnd);

    return allMandatoryFieldsExist && oneOfScoresExists;
  }
};

export const getChallengeTypeTranslation = (
  challengeType: ChallengeType | SamsChallengeType,
  onUnknownReturnOriginal = false,
): string => {
  switch (challengeType) {
    case "antenna":
    case "ANTENNA_CONTACT":
      return "Antenna Touch";
    case "in-out":
    case "BALL_IN_OUT":
      return "Ball In / Out";
    case "blocktouch":
    case "BLOCK_TOUCH":
      return "Block Touch";
    case "nettouch":
    case "NET_FAULT":
      return "Net Fault";
    case "floor-touch":
      return "Floor Touch";
    case "foot-fault":
      return "Foot Fault";
    case "FOOT_FAULT_HALFWAY_LINE":
      return "Foot Fault (Centerline)";
    case "FOOT_FAULT_BASE_LINE":
      return "Foot Fault (Baseline)";
    case "FOOT_FAULT_ATTACK_LINE":
      return "Foot Fault (Attack Line)";
    case "last-touch":
    case "LAST_TOUCH":
      return "Last Touch";
    case "GROUND_CONTACT":
      return "Ground contact";
    case "no-nettouch":
      return "No Net Fault";
    case "no-floor-touch":
      return "No Floor Touch";
    case "no-foot-fault":
      return "No Foot Fault";
    case "no-antenna":
      return "No Antenna Touch";
    default:
      if (onUnknownReturnOriginal) {
        return challengeType;
      } else {
        return `Veraltet (war: ${challengeType})`;
      }
  }
};

export const isChallengeEqual = (
  challenge1: CompletedChallenge | StartedChallenge,
  challenge2: CompletedChallenge | StartedChallenge,
): boolean =>
  challenge1.challenger === challenge2.challenger &&
  challenge1.setScoreBeforeChallenge.team1 ===
    challenge2.setScoreBeforeChallenge.team1 &&
  challenge1.setScoreBeforeChallenge.team2 ===
    challenge2.setScoreBeforeChallenge.team2 &&
  challenge1.setNumber === challenge2.setNumber &&
  challenge1.timestamp === challenge2.timestamp;

export const getScoringTeamFromChallenge = (
  challenge: CompletedChallenge | ChallengeSuccessPayload,
): TeamReference | undefined => {
  if (challenge.challenger === "referee") {
    if (challenge.successful) {
      if (challenge.outcome === "POINT_TEAM1") {
        return "team1";
      }
      if (challenge.outcome === "POINT_TEAM2") {
        return "team2";
      }
    }
  } else if (challenge.successful) {
    return challenge.challenger;
  }
  return undefined;
};

export const getCompletedChallengeType = (
  challenge: StartedChallenge | CompletedChallenge,
):
  | { type: "started"; challenge: StartedChallenge }
  | { type: "successful"; challenge: SuccessfulChallenge }
  | { type: "unsuccessful"; challenge: UnsuccessfulChallenge }
  | { type: "unresolved"; challenge: UnresolvedChallenge }
  | { type: "declined"; challenge: DeclinedChallenge } => {
  if (!("durationMs" in challenge)) {
    return { type: "started", challenge };
  }
  if (challenge.successful) {
    return { type: "successful", challenge };
  }
  if (challenge.unresolved) {
    return { type: "unresolved", challenge };
  }
  if (challenge.declined) {
    return { type: "declined", challenge };
  }
  return { type: "unsuccessful", challenge };
};

export const getChallengeMomentFromChallenge = (
  challenge: Challenge,
): SamsChallengeMoment | undefined => {
  if ("challengeMoment" in challenge) {
    return challenge.challengeMoment;
  } else if (challenge.challenger === "referee") {
    return "AFTER_RALLY";
  } else if (challenge.teamThatLastScored === challenge.challenger) {
    return "DURING_RALLY";
  } else {
    return undefined;
  }
};
